import type { RendererProps } from "../core/element-registry"
import { RenderContext, useRenderContext } from "../core/render-context"
import { ElementRenderer, buildIteratedResolveSymbol, liftV4Props } from "./element-renderer"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { cn } from "../lib/utils"

type TableSectionKind = "body" | "footer" | "header"

// RENDER-PARITY-1 follow-up (2026-08-07) — `renderRow`/`renderCell` used to
// read `row`/`cell` straight off the cogo JSON element: `cell.text`,
// `cell.className`, `cell.align`, `cell.colSpan`. The v4 UI schema
// (packages/cogo-schema/v4/ui.cogo.schema.json,
// packages/cogo-types-v2/src/schema/element.ts) puts `text` (and the rest of
// an element's own visible fields) at the TOP level and reserves `props` for
// island components — but LLM-generated bundles routinely nest them under
// `props` anyway (e.g. `{ type: "table-cell", props: { text: "#{item.x}" } }`,
// the exact shape every table-cell/table-head/table-row in this defect's
// bundle used). Every OTHER renderer that reads free-form element fields
// (`card-renderer.tsx`, `slot-renderer.tsx`, `element-renderer.tsx`'s own
// `createSlotTextRenderer` — which is what "table-head"/"table-cell" resolve
// to when dispatched through the generic registry instead of through this
// file's own row/cell walk) already guards against this with `liftV4Props`
// before reading. `renderRow`/`renderCell` were the one place under a
// table-header/table-body/table-row parent that read raw fields directly —
// so `cell.text` was reliably `undefined` and every cell rendered empty,
// regardless of whether the value was a literal ("Submitter") or an
// item-scoped expression (`#{item.submitter}`). Same fix, same site pattern.
function renderCell(
  cell: RendererProps["element"],
  resolveSymbol: ReturnType<typeof useRenderContext>["resolveSymbol"],
) {
  const lifted = liftV4Props(cell)
  const Cell = lifted.type === "table-head" ? TableHead : TableCell
  const text = lifted.text !== undefined
    ? String(resolveSymbol(String(lifted.text)) ?? "")
    : undefined

  return (
    <Cell
      key={lifted.id}
      className={lifted.className}
      data-cogo-element-id={lifted.id}
      data-cogo-type={lifted.type}
      align={lifted.align}
      colSpan={typeof lifted.colSpan === "number" ? lifted.colSpan : undefined}
      style={lifted.align ? { textAlign: lifted.align } : undefined}
    >
      {text ?? lifted.children?.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))}
    </Cell>
  )
}

function renderRow(
  row: RendererProps["element"],
  resolveSymbol: ReturnType<typeof useRenderContext>["resolveSymbol"],
  key?: string,
) {
  const lifted = liftV4Props(row)
  return (
    <TableRow
      key={key ?? lifted.id}
      className={lifted.className}
      data-cogo-element-id={lifted.id}
      data-cogo-type={lifted.type}
    >
      {lifted.children?.map((cell) => renderCell(cell, resolveSymbol))}
    </TableRow>
  )
}

// RENDER-PARITY-1 follow-up 2 (2026-08-07) — a table-header/table-body/
// table-footer region's own `<table>`/section boundary exists exactly once
// regardless of how many rows the bound collection has (the same rule
// RENDER-PARITY-1 already enforces for an ANCESTOR wrapping one of these
// regions — see element-renderer.tsx's `TABLE_STRUCTURAL_REGION_TYPES`
// guard). That prior fix covers `iterateOver` declared on a container ABOVE
// a region; it does not cover `iterateOver` declared directly ON the region
// itself (e.g. `table-body` iterating its own row template) — that shape
// fell through `ElementRenderer`'s generic per-item clone path
// (`IteratedElement`), which repeats the WHOLE `table-body` — including its
// own `cn-table-structure-host`/`<Table>` wrapper — once per row, producing
// N sibling `<table>`s instead of one table with N rows.
//
// The general fix: this renderer declares `cogoIteratesOwnChildren` (see
// core/element-registry.ts) — a type-level predicate, not a name
// enumeration — telling `ElementRenderer` to skip its generic `iterateOver`
// handling for this element entirely (the same category `block-ref`/
// `carousel` already opt out of by name) and leave `element.iterateOver`/
// `itemVar` intact. This renderer then resolves the collection and builds
// its OWN per-item rows below, directly as `<TableRow>` — never dispatching
// the row template back through the generic per-item component
// (`IteratedElement`/`IteratedContainer`), whose identity-wrapper `<div>` a
// `<tr>` cannot legally contain as a child of `<tbody>`/`<thead>`/`<tfoot>`.
// Each row's cells render inside a `RenderContext.Provider` carrying an
// item-scoped `resolveSymbol`/`itemContext` — built via
// `buildIteratedResolveSymbol` (element-renderer.tsx), the SAME per-item
// `#{itemVar.x}` expression engine (ternary, bracket access, arithmetic,
// multi-`#{}` templates) the generic path uses — so a cell's OWN `text`
// (read directly by `renderCell` below) resolves correctly, AND so does any
// element NESTED inside a cell that dispatches through the generic
// `<ElementRenderer>` (e.g. a status badge reading `#{item.status}` via its
// own `useRenderContext()` call, which finds the nearest provider — this
// one — rather than the region's un-scoped ambient context).
function TableSectionStructureRenderer({
  element,
  kind,
}: RendererProps & { kind: TableSectionKind }) {
  const ctx = useRenderContext()
  const { resolveSymbol } = ctx
  const Section = kind === "header"
    ? TableHeader
    : kind === "footer"
      ? TableFooter
      : TableBody

  if (element.iterateOver) {
    const rawItems = resolveSymbol(element.iterateOver)
    const items = Array.isArray(rawItems) ? rawItems : []
    const rowTemplates = element.children ?? []
    return (
      <div
        className={cn("cn-table-structure-host", element.className)}
        data-table-structure-host={kind}
      >
        <Table>
          <Section>
            {items.flatMap((item, i) => {
              const iterationIdentitySuffix = `${ctx.iterationIdentitySuffix ?? ""}__iter_${i}`
              const itemCtx = {
                ...ctx,
                itemContext: {
                  ...(ctx.itemContext ?? {}),
                  ...(element.itemVar ? { [element.itemVar]: item } : {}),
                  _index: i,
                },
                resolveSymbol: buildIteratedResolveSymbol(item, element.itemVar, i, ctx),
                iterationIdentitySuffix,
              }
              return rowTemplates.map((row, rowIdx) => (
                <RenderContext.Provider key={`${row.id}-${rowIdx}-${i}`} value={itemCtx}>
                  {renderRow(row, itemCtx.resolveSymbol)}
                </RenderContext.Provider>
              ))
            })}
          </Section>
        </Table>
      </div>
    )
  }

  return (
    <div
      className={cn("cn-table-structure-host", element.className)}
      data-table-structure-host={kind}
    >
      <Table>
        <Section>
          {element.children?.map((row) => renderRow(row, resolveSymbol))}
        </Section>
      </Table>
    </div>
  )
}

export function TableHeaderStructureRenderer(props: RendererProps) {
  return <TableSectionStructureRenderer {...props} kind="header" />
}
TableHeaderStructureRenderer.cogoIteratesOwnChildren = true as const

export function TableBodyStructureRenderer(props: RendererProps) {
  return <TableSectionStructureRenderer {...props} kind="body" />
}
TableBodyStructureRenderer.cogoIteratesOwnChildren = true as const

export function TableFooterStructureRenderer(props: RendererProps) {
  return <TableSectionStructureRenderer {...props} kind="footer" />
}
TableFooterStructureRenderer.cogoIteratesOwnChildren = true as const

export function TableRowStructureRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const hasHeaderCell = element.children?.some((child) => child.type === "table-head")
  const Section = hasHeaderCell ? TableHeader : TableBody

  return (
    <div
      className={cn("cn-table-structure-host", element.className)}
      data-table-structure-host="row"
    >
      <Table>
        <Section>
          <TableRow className={element.className}>
            {element.children?.map((cell) => renderCell(cell, resolveSymbol))}
          </TableRow>
        </Section>
      </Table>
    </div>
  )
}
